import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftCircle, Loader2 } from 'lucide-react';
import { 
  collection,
  doc, 
  runTransaction,
  serverTimestamp,
  getDoc 
} from 'firebase/firestore';
import { firestore } from '../firebase';
import LandingPageHeader from "./components/LandingPageHeader";
import LandingPageFooter from './components/LandingPageFooter';

const CustomAlert = ({ variant = 'default', title, description }) => {
  const baseStyles = "rounded-lg border p-4 mb-4";
  const variantStyles = {
    default: "bg-white border-gray-200",
    destructive: "bg-red-50 border-red-200 text-red-800",
    success: "bg-green-50 border-green-200 text-green-800"
  };

  return (
    <div className={`${baseStyles} ${variantStyles[variant]}`}>
      {title && (
        <h5 className="font-medium mb-1">
          {title}
        </h5>
      )}
      {description && (
        <p className="text-sm">
          {description}
        </p>
      )}
    </div>
  );
};

const RefundPage = () => {
  const navigate = useNavigate();
  const [ticketId, setTicketId] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Changed to false initially

  useEffect(() => {
    const fetchTicketDetails = async (id) => {
      setIsLoading(true);
      try {
        const ticketRef = doc(firestore, 'tickets', id);
        const ticketDoc = await getDoc(ticketRef);
        
        if (ticketDoc.exists()) {
          setTicketDetails({ id: ticketDoc.id, ...ticketDoc.data() });
        } else {
          setError('Ticket not found');
          setTimeout(() => navigate('/'), 3000);
        }
      } catch (err) {
        setError('Error fetching ticket details');
      } finally {
        setIsLoading(false);
      }
    };

    if (ticketId) {
      fetchTicketDetails(ticketId);
    }
  }, [ticketId, navigate]);

  const initializeRefund = (id) => {
    setTicketId(id);
  };

  // Show message when no ticket ID is provided
  if (!ticketId) {
    return (
      <div className="min-h-screen bg-yellow-50 flex flex-col">
        <LandingPageHeader />
        <div className="flex-grow p-6">
          <div className="max-w-2xl mx-auto">
            <CustomAlert
              variant="destructive"
              title="No Ticket Selected"
              description="Please select a ticket from the tickets page to proceed with the refund."
            />
            <button 
              onClick={() => navigate('/')} 
              className="mt-4 flex items-center text-gray-600 hover:text-gray-800"
            >
              <ArrowLeftCircle className="w-5 h-5 mr-2" />
              Go to Tickets Page
            </button>
          </div>
        </div>
        <LandingPageFooter />
      </div>
    );
  }

  // Show loading state while fetching ticket details
  if (isLoading) {
    return (
      <div className="min-h-screen bg-yellow-50 flex flex-col">
        <LandingPageHeader />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-800 mx-auto mb-4" />
            <p className="text-gray-600">Loading ticket details...</p>
          </div>
        </div>
        <LandingPageFooter />
      </div>
    );
  }

  // Check if ticket is already refunded
  if (ticketDetails?.paymentStatus === 'REFUNDED' || 
      ticketDetails?.paymentStatus === 'REFUND_IN_PROGRESS') {
    return (
      <div className="min-h-screen bg-yellow-50 flex flex-col">
        <LandingPageHeader />
        <div className="flex-grow p-6">
          <div className="max-w-2xl mx-auto">
            <CustomAlert
              variant="destructive"
              title="Error"
              description="This ticket has already been refunded or is being processed"
            />
            <button 
              onClick={() => navigate('/')} 
              className="mt-4 text-gray-600 hover:text-gray-800"
            >
              Return to Tickets
            </button>
          </div>
        </div>
        <LandingPageFooter />
      </div>
    );
  }

  // Show error if ticket details couldn't be loaded
  if (!ticketDetails) {
    return (
      <div className="min-h-screen bg-yellow-50 flex flex-col">
        <LandingPageHeader />
        <div className="flex-grow p-6">
          <div className="max-w-2xl mx-auto">
            <CustomAlert
              variant="destructive"
              title="Error Loading Ticket"
              description="Unable to load ticket details. Please try again later."
            />
            <button 
              onClick={() => navigate('/')} 
              className="mt-4 flex items-center text-gray-600 hover:text-gray-800"
            >
              <ArrowLeftCircle className="w-5 h-5 mr-2" />
              Return to Tickets
            </button>
          </div>
        </div>
        <LandingPageFooter />
      </div>
    );
  }

  const processWalletRefund = async () => {
    try {
      await runTransaction(firestore, async (transaction) => {
        // Get user document
        const userRef = doc(firestore, 'Users', ticketDetails['user-id']);
        const userDoc = await transaction.get(userRef);

        if (!userDoc.exists()) {
          throw new Error('User not found');
        }

        const currentCredits = parseFloat(userDoc.data().credits || '0');
        const refundAmount = parseFloat(ticketDetails['amount-paid']);

        if (isNaN(currentCredits) || isNaN(refundAmount)) {
          throw new Error('Invalid credit or refund amount');
        }

        const newCredits = (currentCredits + refundAmount).toFixed(2);

        transaction.update(userRef, {
          credits: newCredits
        });

        const ticketRef = doc(firestore, 'tickets', ticketDetails.id);
        transaction.update(ticketRef, {
          paymentStatus: 'REFUNDED_TO_WALLET',
          refundedAt: serverTimestamp()
        });

        const walletTransactionRef = doc(collection(firestore, 'walletTransactions'));
        transaction.set(walletTransactionRef, {
          userId: ticketDetails['user-id'],
          amount: ticketDetails['amount-paid'],
          type: 'REFUND',
          ticketId: ticketDetails.id,
          createdAt: serverTimestamp(),
          status: 'SUCCESS'
        });
      });

      return 'Refund has been processed and added to your wallet';
    } catch (error) {
      console.error('Transaction error:', error);
      throw new Error('Failed to process wallet refund');
    }
  };

  const processOriginalSourceRefund = async () => {
    try {
      await runTransaction(firestore, async (transaction) => {
        const ticketRef = doc(firestore, 'tickets', ticketDetails.id);
        transaction.update(ticketRef, {
          paymentStatus: 'REFUND_IN_PROGRESS',
          refundInitiatedAt: serverTimestamp()
        });

        const refundRef = doc(collection(firestore, 'refunds'));
        transaction.set(refundRef, {
          ticketId: ticketDetails.id,
          userId: ticketDetails['user-id'],
          amount: ticketDetails['amount-paid'],
          type: 'ORIGINAL_SOURCE',
          status: 'IN_PROGRESS',
          createdAt: serverTimestamp()
        });
      });

      return 'Refund request initiated. Amount will be credited to your original payment source in 5-7 business days';
    } catch (error) {
      console.error('Transaction error:', error);
      throw new Error('Failed to initiate refund to original source');
    }
  };

  const handleRefund = async (refundType) => {
    setLoading(true);
    setError('');
    try {
      const message = await (refundType === 'wallet' 
        ? processWalletRefund() 
        : processOriginalSourceRefund());
      
      setSuccess(message);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-yellow-50 flex flex-col">
      <LandingPageHeader />
      
      <main className="flex-grow p-6">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center text-gray-600 mb-6 hover:text-gray-800"
          >
            <ArrowLeftCircle className="w-5 h-5 mr-2" />
            Back to Tickets
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">Refund Options</h1>
            
            {error && (
              <CustomAlert
                variant="destructive"
                title="Error"
                description={error}
              />
            )}

            {success && (
              <CustomAlert
                variant="success"
                title="Success"
                description={success}
              />
            )}

            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-700 mb-2">Ticket Details</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="mb-2">
                  <span className="font-medium">Screen:</span> {ticketDetails.screenName}
                </p>
                <p className="mb-2">
                  <span className="font-medium">Seat:</span> {ticketDetails.seatNo}
                </p>
                <p className="mb-2">
                  <span className="font-medium">Amount:</span> ₹{ticketDetails['amount-paid']}
                </p>
                <p className="mb-2">
                  <span className="font-medium">Class:</span> {ticketDetails.class}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => handleRefund('wallet')}
                disabled={loading}
                className="w-full bg-red-800 text-white py-3 px-6 rounded-xl hover:bg-red-700 transition duration-300 disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Refund to Wallet (Instant)'
                )}
              </button>

              <button
                onClick={() => handleRefund('original')}
                disabled={loading}
                className="w-full bg-gray-800 text-white py-3 px-6 rounded-xl hover:bg-gray-700 transition duration-300 disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Refund to Original Source (5-7 business days)'
                )}
              </button>
            </div>

            <div className="mt-6 space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Wallet Refund:</span> Instantly credited to your wallet balance
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Original Source Refund:</span> 5-7 business days processing time
              </p>
              <p className="text-sm text-gray-600 italic">
                Note: Wallet credits can be used for future bookings on our platform
              </p>
            </div>
          </div>
        </div>
      </main>

      <LandingPageFooter />
    </div>
  );
};

export default RefundPage;